import {
  BulkFieldEditDialog,
  type BulkFieldSpec,
  type BulkResult,
} from '@/components/common/BulkFieldEditDialog';
import {
  maintenanceScheduleApi,
  type BulkScheduleChanges,
} from '@/api/services/maintenance-schedule.api';

/**
 * Fields a bulk edit may set on maintenance schedules.
 *
 * The two operations this exists for are pausing a run of schedules while an
 * asset is down, and pushing their next due date. Equipment is deliberately not
 * editable here — a schedule is maintenance for a particular asset, so pointing
 * several at one asset is a mistake rather than an intent.
 */
const FIELDS: BulkFieldSpec[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'PAUSED', label: 'Paused' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELLED', label: 'Cancelled' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    options: [
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
      { value: 'CRITICAL', label: 'Critical' },
    ],
  },
  {
    key: 'frequency',
    label: 'Frequency',
    type: 'select',
    options: [
      { value: 'DAILY', label: 'Daily' },
      { value: 'WEEKLY', label: 'Weekly' },
      { value: 'BIWEEKLY', label: 'Biweekly' },
      { value: 'MONTHLY', label: 'Monthly' },
      { value: 'QUARTERLY', label: 'Quarterly' },
      { value: 'SEMI_ANNUAL', label: 'Semi-annual' },
      { value: 'ANNUAL', label: 'Annual' },
      { value: 'HOURS_BASED', label: 'Hours based' },
      { value: 'CONDITION_BASED', label: 'Condition based' },
    ],
  },
  {
    key: 'intervalDays',
    label: 'Interval (days)',
    type: 'number',
    hint: 'Condition-based schedules are not driven by an interval and will be skipped',
  },
  { key: 'nextDue', label: 'Next due', type: 'date' },
];

interface BulkScheduleEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleIds: string[];
  onApplied: (result: BulkResult) => void;
}

export function BulkScheduleEditDialog({
  open,
  onOpenChange,
  scheduleIds,
  onApplied,
}: BulkScheduleEditDialogProps) {
  return (
    <BulkFieldEditDialog
      open={open}
      onOpenChange={onOpenChange}
      ids={scheduleIds}
      fields={FIELDS}
      noun="schedule"
      submit={(ids, changes) =>
        maintenanceScheduleApi.bulkUpdate({
          scheduleIds: ids,
          changes: changes as BulkScheduleChanges,
        })
      }
      describeRow={(row) => ({
        title: (row.name as string) ?? row.id,
        subtitle: row.equipmentName as string | undefined,
      })}
      onApplied={onApplied}
    />
  );
}
